import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);

      const dialogComponent = document.getElementById('quick-add-dialog');
      if (dialogComponent instanceof QuickAddDialog) {
        await dialogComponent.initializeAzoraProduct(currentUrl);
      }
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;
    if (productGrid.querySelector('azora-order-table')) {
      const moduleScript = productGrid.querySelector('script[src*="/azora-order-table.js"]');
      if (moduleScript) await import(moduleScript.src);
      productGrid.querySelectorAll('.product-details h1').forEach(title => { if (!title.closest('.view-product-title')) title.remove(); });
      productGrid.querySelectorAll('slideshow-controls[thumbnails]').forEach(controls => {
        for (const direction of [-1, 1]) {
          const button = document.createElement('button');
          button.type = 'button';button.className = 'azora-thumb-scroll';
          button.textContent = direction < 0 ? '⌃' : '⌄';
          button.setAttribute('aria-label', direction < 0 ? 'Previous image' : 'Next image');
          button.addEventListener('click', event => {
            event.preventDefault();
            event.stopPropagation();
            const slideshow = controls.closest('slideshow-component');
            const count = slideshow?.slides?.length || 0;
            if (count < 2) return;
            const index = (slideshow.current + direction + count) % count;
            slideshow.select(index, event, {animate:false});
          });
          direction < 0 ? controls.prepend(button) : controls.append(button);
        }
      });
      const order = productGrid.querySelector('azora-order-table');
      const add = order.querySelector('[data-add]');
      const scroll = document.createElement('div');
      scroll.className = 'azora-quick-options-scroll';
      scroll.setAttribute('tabindex', '0');
      scroll.setAttribute('aria-label', 'Product options and quantities');
      for (const child of [...order.children]) {
        if (child !== add && child.tagName !== 'SCRIPT') scroll.append(child);
      }
      order.append(scroll, add);
      modalContent.replaceChildren(...productGrid.childNodes);
      return;
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price, .azora-price-container');
      const productTitle = document.createElement('a');
      productTitle.className = 'product-title';
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        const sourcePrice =
          productPrice.querySelector('.azora-normal-price, .price-item--sale, .price-item--regular, .price') ||
          productPrice;
        const visiblePrice = document.createElement('div');
        visiblePrice.className = 'azora-quick-price';
        visiblePrice.setAttribute('aria-live', 'polite');
        visiblePrice.textContent = sourcePrice.textContent?.replace(/\s+/g, ' ').trim() || '';
        visiblePrice.style.cssText =
          'display:block!important;visibility:visible!important;opacity:1!important;color:#111!important;font-size:16px!important;line-height:1.4!important;margin:4px 0 12px!important;position:relative!important;z-index:20!important;';
        productHeader.appendChild(visiblePrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    } else {
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price, .azora-price-container');

      if (variantPicker && productPrice && !productGrid.querySelector('.azora-quick-price')) {
        const sourcePrice =
          productPrice.querySelector('.azora-normal-price, .price-item--sale, .price-item--regular, .price') ||
          productPrice;
        const visiblePrice = document.createElement('div');
        visiblePrice.className = 'azora-quick-price';
        visiblePrice.setAttribute('aria-live', 'polite');
        visiblePrice.textContent = sourcePrice.textContent?.replace(/\s+/g, ' ').trim() || '';
        visiblePrice.style.cssText =
          'display:block!important;visibility:visible!important;opacity:1!important;color:#111!important;font-size:16px!important;line-height:1.4!important;margin:4px 0 12px!important;position:relative!important;z-index:20!important;';
        variantPicker.insertAdjacentElement('beforebegin', visiblePrice);
      }
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    this.setAttribute('data-quick-add-button', 'choose');
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #productData = null;
  #selectedVariant = null;
  #productImages = [];
  #imageIndex = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleAzoraVariantUpdate);
    this.addEventListener('click', this.#handleAzoraModeClick, { signal: this.#abortController.signal });
    this.addEventListener('change', this.#handleAzoraQuantityChange, { signal: this.#abortController.signal });

    this.#installAzoraStyles();

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleAzoraModeClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const optionButton = event.target.closest('[data-azora-option-index]');
    if (optionButton instanceof HTMLButtonElement && this.contains(optionButton)) {
      event.preventDefault();
      this.#selectAzoraOption(optionButton);
      return;
    }

    const imageButton = event.target.closest('[data-azora-image-direction]');
    if (imageButton instanceof HTMLButtonElement && this.contains(imageButton)) {
      event.preventDefault();
      const direction = imageButton.dataset.azoraImageDirection === 'next' ? 1 : -1;
      this.#changeAzoraImage(direction);
      return;
    }

    const button = event.target.closest('[data-azora-mode]');
    if (!(button instanceof HTMLElement) || !this.contains(button)) return;

    const mode = button.dataset.azoraMode === 'bulk' ? 'bulk' : 'sample';
    this.dataset.azoraMode = mode;
    this.#applyAzoraMode(mode);
  };

  #handleAzoraQuantityChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.matches('input[name="quantity"]') || !this.contains(event.target)) return;

    const buyBlock = event.target.closest('.buy-buttons-block');
    if (!(buyBlock instanceof HTMLElement)) return;

    const modeWrapper = buyBlock.querySelector('[data-azora-purchase-mode]');
    const mode = modeWrapper instanceof HTMLElement && modeWrapper.dataset.azoraCurrentMode === 'bulk' ? 'bulk' : 'sample';

    if (mode === 'bulk') {
      const bulkMin = Number.parseInt(modeWrapper?.dataset.bulkMin || '2', 10);
      const currentQuantity = Number.parseInt(event.target.value || '0', 10);
      if (!Number.isNaN(bulkMin) && currentQuantity < bulkMin) event.target.value = String(bulkMin);
    }

    this.#updateAzoraPrice(buyBlock, mode);
  };

  #handleAzoraVariantUpdate = (event) => {
    const mode = this.dataset.azoraMode === 'bulk' ? 'bulk' : 'sample';

    requestAnimationFrame(() => {
      const newHtml = event.detail?.data?.html;
      const variantJson = newHtml?.querySelector?.('variant-picker script[type="application/json"]')?.textContent;
      if (variantJson) {
        try {
          const variant = JSON.parse(variantJson);
          this.#selectedVariant = variant;
          this.#updateAzoraVariant(variant);
        } catch (_error) {
          this.#syncAzoraVariantFromForm();
        }
      } else {
        this.#syncAzoraVariantFromForm();
      }

      this.#applyAzoraMode(mode);
    });
  };

  async initializeAzoraProduct(productPageUrl) {
    if (this.querySelector("azora-order-table")) return;
    this.#productData = await this.#fetchAzoraProduct(productPageUrl);
    if (!this.#productData) return;

    const url = new URL(productPageUrl, window.location.origin);
    const requestedVariantId = url.searchParams.get('variant');
    const formVariantId = this.querySelector('input[name="id"]')?.value;
    const variantId = requestedVariantId || formVariantId;

    this.#selectedVariant =
      this.#productData.variants.find((variant) => String(variant.id) === String(variantId)) ||
      this.#productData.variants.find((variant) => variant.available) ||
      this.#productData.variants[0];

    this.#productImages = (this.#productData.images || [])
      .map((image) => (typeof image === 'string' ? image : image?.src))
      .filter(Boolean);

    this.#renderAzoraVariantPicker();
    this.#mountAzoraImage();
    this.#cleanupAzoraDuplicates();
    this.#ensureAzoraPrice();
    this.#updateAzoraVariant(this.#selectedVariant);
    this.#applyAzoraMode(this.dataset.azoraMode === 'bulk' ? 'bulk' : 'sample');
  }

  #ensureAzoraPrice() {
    const buyBlock = this.querySelector('.buy-buttons-block');
    if (!(buyBlock instanceof HTMLElement)) return;

    let price = this.querySelector('.azora-quick-price');
    if (!(price instanceof HTMLElement)) {
      price = document.createElement('div');
      price.className = 'azora-quick-price';
      price.setAttribute('aria-live', 'polite');
    }

    price.style.cssText =
      'display:block!important;visibility:visible!important;opacity:1!important;color:#111!important;font-size:16px!important;line-height:1.4!important;margin:4px 0 6px!important;position:relative!important;z-index:20!important;';
    buyBlock.prepend(price);

    let details = buyBlock.querySelector('.azora-quick-price-details');
    if (!(details instanceof HTMLElement)) {
      details = document.createElement('div');
      details.className = 'azora-quick-price-details';
      for (const text of ['Wholesale', 'Shipping calculated at checkout.']) {
        const line = document.createElement('div');
        line.textContent = text;
        line.style.cssText = 'display:block!important;margin:0!important;font-size:12px!important;line-height:1.6!important;color:#555!important;';
        details.append(line);
      }
    }
    details.style.cssText = 'display:block!important;width:100%!important;margin:0 0 16px!important;padding:0 0 18px!important;border-bottom:1px solid #888!important;';
    price.insertAdjacentElement('afterend', details);

    const originalPrice = this.#selectedVariant?.price;
    if (Number.isFinite(Number(originalPrice)) && Number(originalPrice) > 0) {
      price.textContent = this.#formatMoney(Number(originalPrice));
    }
  }

  #cleanupAzoraDuplicates() {
    const details = this.querySelector('.product-details');
    if (!(details instanceof HTMLElement)) return;

    const seenTitles = new Set();
    details.querySelectorAll('h1, h2, .product-title, .product-header__title').forEach((title) => {
      const normalizedTitle = title.textContent?.replace(/\s+/g, ' ').trim().toLowerCase();
      if (!normalizedTitle) return;
      if (seenTitles.has(normalizedTitle)) title.remove();
      else seenTitles.add(normalizedTitle);
    });
  }

  async #fetchAzoraProduct(productPageUrl) {
    try {
      const url = new URL(productPageUrl, window.location.origin);
      url.search = '';
      url.hash = '';
      url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      return await response.json();
    } catch (_error) {
      return null;
    }
  }

  #renderAzoraVariantPicker() {
    if (!this.#productData || this.#productData.variants.length < 2) return;

    const buyBlock = this.querySelector('.buy-buttons-block');
    if (!(buyBlock instanceof HTMLElement)) return;

    const existing = buyBlock.querySelector('.azora-quick-variant-picker');
    existing?.remove();

    const picker = document.createElement('div');
    picker.className = 'azora-quick-variant-picker';
    picker.setAttribute('aria-label', 'Product options');

    this.#productData.options.forEach((option, optionIndex) => {
      const optionName = typeof option === 'string' ? option : option.name;
      if (!optionName || optionName.toLowerCase() === 'title') return;
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'azora-quick-option';

      const legend = document.createElement('legend');
      legend.textContent = optionName;
      fieldset.appendChild(legend);

      const values = [
        ...new Set(
          this.#productData.variants.map((variant) => variant[`option${optionIndex + 1}`]).filter(Boolean)
        ),
      ];

      const selectedValue = this.#selectedVariant?.[`option${optionIndex + 1}`];
      values.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'azora-quick-option__button';
        button.dataset.azoraOptionIndex = String(optionIndex);
        button.dataset.azoraOptionValue = value;
        button.textContent = value;
        button.classList.toggle('is-selected', value === selectedValue);
        button.setAttribute('aria-pressed', value === selectedValue ? 'true' : 'false');
        fieldset.appendChild(button);
      });

      picker.appendChild(fieldset);
    });

    const modeWrapper = buyBlock.querySelector('[data-azora-purchase-mode]');
    buyBlock.insertBefore(picker, modeWrapper || buyBlock.firstChild);
  }

  #mountAzoraImage() {
    const media = this.querySelector('.product-information__media');
    if (!(media instanceof HTMLElement)) return;

    const originalImage = media.querySelector('img');
    const frame = document.createElement('div');
    const image = document.createElement('img');
    const previousButton = document.createElement('button');
    const nextButton = document.createElement('button');

    frame.className = 'azora-quick-main-image';
    image.className = 'azora-quick-main-image__image';
    image.alt = originalImage?.alt || this.#productData?.title || '';
    image.loading = 'eager';

    previousButton.type = 'button';
    previousButton.className = 'azora-quick-main-image__arrow azora-quick-main-image__arrow--previous';
    previousButton.dataset.azoraImageDirection = 'previous';
    previousButton.setAttribute('aria-label', 'Previous product image');
    previousButton.textContent = '‹';

    nextButton.type = 'button';
    nextButton.className = 'azora-quick-main-image__arrow azora-quick-main-image__arrow--next';
    nextButton.dataset.azoraImageDirection = 'next';
    nextButton.setAttribute('aria-label', 'Next product image');
    nextButton.textContent = '›';

    const originalSource = originalImage?.currentSrc || originalImage?.src;
    if (originalSource) image.src = originalSource;

    frame.appendChild(image);
    if (this.#productImages.length > 1) {
      frame.appendChild(previousButton);
      frame.appendChild(nextButton);
    }
    media.replaceChildren(frame);
  }

  #changeAzoraImage(direction) {
    if (this.#productImages.length < 2) return;

    this.#imageIndex = (this.#imageIndex + direction + this.#productImages.length) % this.#productImages.length;
    this.#updateAzoraImage(this.#productImages[this.#imageIndex], this.#productData?.title || '');
  }

  #selectAzoraOption(button) {
    if (!this.#productData || !this.#selectedVariant) return;

    const optionIndex = Number.parseInt(button.dataset.azoraOptionIndex || '0', 10);
    const selectedOptions = this.#productData.options.map(
      (_option, index) => this.#selectedVariant[`option${index + 1}`]
    );
    selectedOptions[optionIndex] = button.dataset.azoraOptionValue;

    const exactVariant = this.#productData.variants.find((variant) =>
      selectedOptions.every((value, index) => variant[`option${index + 1}`] === value)
    );
    const fallbackVariant = this.#productData.variants.find(
      (variant) => variant[`option${optionIndex + 1}`] === button.dataset.azoraOptionValue && variant.available
    );
    const variant = exactVariant || fallbackVariant;
    if (!variant) return;

    this.#selectedVariant = variant;
    this.#updateAzoraVariant(variant);
  }

  #syncAzoraVariantFromForm() {
    if (!this.#productData) return;
    const variantId = this.querySelector('input[name="id"]')?.value;
    const variant = this.#productData.variants.find((item) => String(item.id) === String(variantId));
    if (!variant) return;
    this.#selectedVariant = variant;
    this.#updateAzoraVariant(variant);
  }

  #updateAzoraVariant(variant) {
    if (!variant) return;

    this.querySelectorAll('input[name="id"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.value = String(variant.id);
      input.disabled = !variant.available;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    this.querySelectorAll('.azora-quick-option__button').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      const index = Number.parseInt(button.dataset.azoraOptionIndex || '0', 10);
      const selected = button.dataset.azoraOptionValue === variant[`option${index + 1}`];
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    const imageUrl = variant.featured_image?.src || variant.featured_image || variant.featured_media?.preview_image?.src;
    if (imageUrl) this.#updateAzoraImage(imageUrl, variant.featured_image?.alt || this.#productData?.title || '');

    const priceContainer = this.querySelector('.azora-price-container');
    if (priceContainer instanceof HTMLElement) {
      priceContainer.dataset.price = String(variant.price);
      priceContainer.dataset.priceCents = String(variant.price);
      priceContainer.dataset.wholesalePriceCents = String(Math.round(variant.price * 0.95));
    }

    const buyBlock = this.querySelector('.buy-buttons-block');
    if (buyBlock instanceof HTMLElement) {
      this.#updateAzoraPrice(buyBlock, this.dataset.azoraMode === 'bulk' ? 'bulk' : 'sample');
    }
  }

  #updateAzoraImage(imageUrl, alt) {
    const media = this.querySelector('.product-information__media');
    if (!(media instanceof HTMLElement)) return;

    const image = media.querySelector('.azora-quick-main-image__image');
    if (!(image instanceof HTMLImageElement)) return;

    const normalizedUrl = imageUrl.startsWith('//') ? `${window.location.protocol}${imageUrl}` : imageUrl;
    image.src = normalizedUrl;
    image.alt = alt;

    const normalizedTarget = normalizedUrl.split('?')[0];
    const matchingIndex = this.#productImages.findIndex((item) => {
      const itemUrl = item.startsWith('//') ? `${window.location.protocol}${item}` : item;
      return itemUrl.split('?')[0] === normalizedTarget;
    });
    if (matchingIndex >= 0) this.#imageIndex = matchingIndex;
  }

  #applyAzoraMode(mode) {
    const buyBlock = this.querySelector('.buy-buttons-block');
    if (!(buyBlock instanceof HTMLElement)) return;

    const modeWrapper = buyBlock.querySelector('[data-azora-purchase-mode]');
    const quantityInput = buyBlock.querySelector('input[name="quantity"]');
    if (!(modeWrapper instanceof HTMLElement) || !(quantityInput instanceof HTMLInputElement)) return;

    if (!quantityInput.dataset.azoraOriginalMin) {
      quantityInput.dataset.azoraOriginalMin = quantityInput.getAttribute('min') || '1';
    }

    modeWrapper.dataset.azoraCurrentMode = mode;
    modeWrapper.querySelectorAll('[data-azora-mode]').forEach((button) => {
      const active = button instanceof HTMLElement && button.dataset.azoraMode === mode;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    const originalMin = Number.parseInt(quantityInput.dataset.azoraOriginalMin || '1', 10);

    if (mode === 'bulk') {
      const bulkMin = Number.parseInt(modeWrapper.dataset.bulkMin || '2', 10);
      const minimum = Math.max(Number.isNaN(originalMin) ? 1 : originalMin, Number.isNaN(bulkMin) ? 2 : bulkMin);
      const currentQuantity = Number.parseInt(quantityInput.value || '0', 10);
      quantityInput.min = String(minimum);
      if (Number.isNaN(currentQuantity) || currentQuantity < minimum) quantityInput.value = String(minimum);
    } else {
      const minimum = Number.isNaN(originalMin) ? 1 : Math.max(1, originalMin);
      quantityInput.min = String(minimum);
      quantityInput.value = String(minimum);
    }

    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.#updateAzoraPrice(buyBlock, mode);
  }

  #updateAzoraPrice(buyBlock, mode) {
    const productArea =
      buyBlock.closest('product-information') ||
      buyBlock.closest('.product-information') ||
      buyBlock.closest('[data-product-information]') ||
      this;

    const quantityInput = buyBlock.querySelector('input[name="quantity"]');
    if (!(quantityInput instanceof HTMLInputElement)) return;

    const priceContainer = productArea.querySelector('.azora-price-container');
    const dataPrice =
      priceContainer instanceof HTMLElement
        ? priceContainer.dataset.priceCents || priceContainer.dataset.price || ''
        : '';
    const basePrice = Number.parseInt(String(this.#selectedVariant?.price || dataPrice || '0'), 10);
    if (!Number.isFinite(basePrice) || basePrice <= 0) return;

    const displayedUnitPrice = Math.round(basePrice * (mode === 'bulk' ? 0.95 : 1));
    const formattedPrice = this.#formatMoney(displayedUnitPrice);
    const quickPriceTargets = this.querySelectorAll('.azora-quick-price');
    const normalPriceTargets = productArea.querySelectorAll('.azora-normal-price');
    const wholesalePriceTargets = productArea.querySelectorAll('.azora-wholesale-price');

    quickPriceTargets.forEach((target) => {
      target.textContent = formattedPrice;
      target.removeAttribute('hidden');
      if (target instanceof HTMLElement) target.style.display = 'block';
    });

    normalPriceTargets.forEach((target) => {
      target.textContent = formattedPrice;
      target.hidden = mode === 'bulk';
      target.classList.toggle('azora-price-hidden', mode === 'bulk');
      if (target instanceof HTMLElement) target.style.display = mode === 'bulk' ? 'none' : '';
    });

    wholesalePriceTargets.forEach((target) => {
      target.textContent = formattedPrice;
      target.hidden = mode !== 'bulk';
      target.classList.toggle('azora-price-hidden', mode !== 'bulk');
      if (target instanceof HTMLElement) target.style.display = mode === 'bulk' ? '' : 'none';
    });

    if (!quickPriceTargets.length && !normalPriceTargets.length && !wholesalePriceTargets.length) {
      const priceTargets = productArea.querySelectorAll(
        'product-price .price-item--regular, product-price .price-item--sale, product-price [ref="price"]'
      );
      priceTargets.forEach((target, index) => {
        target.textContent = formattedPrice;
        target.classList.toggle('azora-price-hidden', index > 0);
        if (target instanceof HTMLElement) target.style.display = index === 0 ? '' : 'none';
      });
    }

    if (priceContainer instanceof HTMLElement) {
      priceContainer.dataset.azoraMode = mode;
      priceContainer.dataset.azoraQuantity = quantityInput.value || '1';
    }

  }

  #installAzoraStyles() {
    if (document.getElementById('azora-quick-add-fixes')) return;

    const style = document.createElement('style');
    style.id = 'azora-quick-add-fixes';
    style.textContent = `
      quick-add-dialog .quick-add-modal__content,
      quick-add-dialog [data-product-grid-content],
      quick-add-dialog .product-information,
      quick-add-dialog .product-information__grid {
        align-items: start !important;
        height: 100% !important;
        padding-block-start: 0 !important;
        margin-block-start: 0 !important;
        min-height: 0 !important;
      }

      quick-add-dialog .product-details {
        align-self: start !important;
        justify-content: flex-start !important;
        align-content: start !important;
        gap: 0 !important;
        min-height: 0 !important;
        height: auto !important;
        max-height: none !important;
        padding-block-start: 16px !important;
        overflow: visible !important;
        scrollbar-width: none;
      }

      quick-add-dialog .product-details::-webkit-scrollbar {
        display: none;
      }

      quick-add-dialog .product-details h1,
      quick-add-dialog .product-details h2,
      quick-add-dialog .product-title,
      quick-add-dialog .product-header__title {
        margin-block: 0 8px !important;
        color: #111 !important;
        opacity: 1 !important;
        filter: none !important;
        text-shadow: none !important;
      }

      quick-add-dialog .buy-buttons-block {
        display: block !important;
        width: 100% !important;
        margin-block-start: 10px !important;
      }

      quick-add-dialog product-price,
      quick-add-dialog .azora-price-container,
      quick-add-dialog .azora-quick-price {
        position: relative !important;
        z-index: 1 !important;
        display: block !important;
        min-height: 24px !important;
        margin: 6px 0 10px !important;
        padding: 0 !important;
        overflow: visible !important;
        line-height: 1.45 !important;
      }

      quick-add-dialog product-price .price,
      quick-add-dialog .azora-price-container .price {
        display: inline-block !important;
        min-height: 22px !important;
        overflow: visible !important;
        line-height: 1.45 !important;
      }

      quick-add-dialog .azora-quick-price {
        color: #111 !important;
        font-size: 16px !important;
        font-weight: 400 !important;
        opacity: 1 !important;
        visibility: visible !important;
      }

      quick-add-dialog .azora-price-hidden {
        display: none !important;
      }

      quick-add-dialog .azora-purchase-mode {
        width: 92% !important;
        max-width: 390px !important;
        gap: 10px !important;
        margin-bottom: 12px !important;
      }

      quick-add-dialog .azora-purchase-mode__button {
        min-height: 50px !important;
        padding: 7px 11px !important;
      }

      quick-add-dialog .product-information__media {
        align-self: start !important;
        display: block !important;
        height: 100% !important;
        max-height: none !important;
        min-height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden !important;
      }

      quick-add-dialog .azora-quick-main-image {
        position: relative;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        width: 100% !important;
        height: 100% !important;
        max-height: none !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden;
      }

      quick-add-dialog .azora-quick-main-image__image {
        display: block !important;
        width: 100% !important;
        height: 100% !important;
        max-height: none !important;
        object-fit: cover !important;
        object-position: center center !important;
      }

      quick-add-dialog .azora-quick-main-image__arrow {
        position: absolute;
        top: 50%;
        z-index: 2;
        display: grid;
        place-items: center;
        width: 36px;
        height: 36px;
        padding: 0;
        border: 1px solid rgba(0, 0, 0, 0.14);
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.92);
        color: #111;
        font-size: 25px;
        line-height: 1;
        cursor: pointer;
        transform: translateY(-50%);
      }

      quick-add-dialog .azora-quick-main-image__arrow--previous {
        left: 14px;
      }

      quick-add-dialog .azora-quick-main-image__arrow--next {
        right: 14px;
      }

      quick-add-dialog .azora-quick-variant-picker {
        display: grid;
        gap: 10px;
        width: 100%;
        margin: 0 0 12px;
      }

      quick-add-dialog .azora-quick-option {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        min-width: 0;
        margin: 0;
        padding: 0;
        border: 0;
      }

      quick-add-dialog .azora-quick-option legend {
        flex: 0 0 100%;
        margin-bottom: 0;
        padding: 0;
        font-size: 13px;
        font-weight: 700;
        text-transform: uppercase;
        line-height: 1.2;
      }

      quick-add-dialog .azora-quick-option__button {
        flex: 0 0 auto;
        min-width: 76px;
        min-height: 40px;
        padding: 6px 11px;
        border: 1px solid #d4dada;
        background: #edf2f3;
        color: #222;
        cursor: pointer;
      }

      quick-add-dialog .azora-quick-option__button.is-selected {
        border-color: #ff7da8;
        background: #ddcbd2;
        color: #fff;
        box-shadow: none;
      }

      quick-add-dialog .add-to-cart-button,
      quick-add-dialog button[name='add'].add-to-cart-button {
        border-color: #dbe7e5 !important;
        background: #dbe7e5 !important;
        color: #111 !important;
        transition: background-color 180ms ease, border-color 180ms ease !important;
      }

      quick-add-dialog .add-to-cart-button:hover,
      quick-add-dialog .add-to-cart-button:focus-visible,
      quick-add-dialog button[name='add'].add-to-cart-button:hover,
      quick-add-dialog button[name='add'].add-to-cart-button:focus-visible {
        border-color: #e2abc4 !important;
        background: #e2abc4 !important;
      }

      quick-add-dialog .product-details,
      quick-add-dialog .buy-buttons-block {
        position: static !important;
        inset: auto !important;
        transform: none !important;
      }

      @media (min-width: 750px) {
        quick-add-dialog .quick-add-modal {
          height: max-content !important;
          max-height: none !important;
          overflow: visible !important;
        }
        quick-add-dialog .quick-add-modal__content {
          height: max-content !important;
          max-height: none !important;
          overflow: visible !important;
          align-items: stretch !important;
        }
        quick-add-dialog .product-information__media {
          position: relative !important;
          align-self: stretch !important;
          height: auto !important;
        }
        quick-add-dialog .azora-quick-main-image {
          position: absolute !important;
          inset: 0 !important;
        }
        quick-add-dialog .product-details {
          padding-block-start: 8px !important;
        }
        quick-add-dialog .view-product-title {
          white-space: normal !important;
          overflow: visible !important;
          text-overflow: clip !important;
        }
      }

      @media (min-width: 750px) and (max-height: 740px) {
        quick-add-dialog .product-details { padding-block-start: 0 !important; }
        quick-add-dialog .view-product-title { margin-bottom: 4px !important; }
        quick-add-dialog .buy-buttons-block {
          margin-block-start: 4px !important;
          padding-bottom: 14px !important;
        }
        quick-add-dialog .azora-quick-price-details {
          margin-bottom: 10px !important;
          padding-bottom: 10px !important;
        }
        quick-add-dialog .azora-quick-variant-picker { gap: 8px; margin-bottom: 10px; }
        quick-add-dialog .azora-quick-option { gap: 6px; }
        quick-add-dialog .azora-quick-option__button { min-height: 34px; padding-block: 4px; }
        quick-add-dialog .azora-purchase-mode { margin-bottom: 8px !important; }
        quick-add-dialog .product-form-buttons { gap: 8px !important; }
      }

      @media (max-width: 749px) {
        quick-add-dialog .azora-quick-main-image,
        quick-add-dialog .azora-quick-main-image__image {
          height: 58vh !important;
          max-height: 58vh !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  #formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }

    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en-US';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  }

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
